import React, {useState} from "react";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import {Grid, Typography, Card, CardHeader, CardContent} from "@mui/material";
import Loading from "../components/common/Loading";
import {useTranslation} from "react-i18next";
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import {useMutation, useQuery} from "@apollo/client";
import {formatDate} from "../common/DateUtil";
import {GET_CURRENT_RESERVATIONS, GET_TODAY_RESERVATION_OCCUPATION, GET_UPCOMING_RESERVATIONS, SUBMIT_ATTENTION} from "../gql/dashboard.gql";
import AttentionModal from "../components/common/AttentionModal";
import AppSnackbar from "../common/AppSnackbar";

const ReservationsDashboard = () => {
    const {data: occupationData} = useQuery(GET_TODAY_RESERVATION_OCCUPATION);
    const {data: currentReservationData} = useQuery(GET_CURRENT_RESERVATIONS);
    const {data: upcomingReservationData} = useQuery(GET_UPCOMING_RESERVATIONS, {
        variables: {days: 7}
    });

    const [addAttention] = useMutation(SUBMIT_ATTENTION);
    const {t} = useTranslation();
    const pad = (n: number) => String(n).padStart(2, '0');
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    // action modal attention
    const [open, setOpen] = React.useState(false);
    const [currentUuid, setCurrentUuid] = useState(undefined);
    const [attentionSubject, setAttentionSubject] = React.useState(null);
    const [currentAnimalName, setCurrentAnimalName] = React.useState(null);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setAttentionSubject(null);
        setOpen(false);
    }
    // end
    const todayCheckIns = currentReservationData?.currentReservations
        ?.filter(r => r.fromDate === todayStr)
        ?.flatMap(r => r.animals) || [];

    const todayCheckOuts = currentReservationData?.currentReservations
        ?.filter(r => r.toDate === todayStr)
        ?.flatMap(r => r.animals) || [];

    function handleSubmitAttention(subjectArg) {
        const subjectToUse = subjectArg ?? attentionSubject;
        addAttention({
            variables: {
                animalUuid: currentUuid,
                subject: subjectToUse,
            },
            refetchQueries: [
                {query: GET_CURRENT_RESERVATIONS}
            ],
            awaitRefetchQueries: true,
        }).then(() => {
            setSnackbarMessage(t('attention.save.success', 'Aandachtspunt opgeslagen'));
            setSeverity('success');
            setOpenSnackbar(true);
        }).catch(() => {
            setSnackbarMessage(t('attention.save.error', 'Aandachtspunt opslaan mislukt'));
            setSeverity('error');
            setOpenSnackbar(true);
        }).finally(() => handleClose());
    }

    return (
        <Grid container justifyContent="center" sx={{px: {xs: 1, sm: 2}, py: {xs: 1, sm: 2}}}>
            <Grid item xs={12} sm={4} sx={{mb: 2}}>
                <Card variant="outlined" sx={{p: 2, m: 2, height: '100%'}}>
                    <CardHeader title={<Typography color="secondary" variant="h5" align={"center"}>{t('currentOccupancy')}</Typography>}/>
                    <Grid container justifyContent="center" spacing={1} alignItems="center" sx={{textAlign: 'center'}}>
                        <CardContent>
                            <Typography color="primary" variant="h1">
                                {`${occupationData?.reservationOccupations}%`}
                            </Typography>
                        </CardContent>
                    </Grid>
                </Card>
            </Grid>
            <Grid item xs={12} sm={4} sx={{mb: 2}}>
                <Card variant="outlined" sx={{p: 2, m: 2, height: '100%'}}>
                    <CardHeader title={<Typography color="secondary" variant="h5" align={"center"}>{t('ongoingReservations')}</Typography>}/>
                    <Grid container justifyContent="center" spacing={1} sx={{textAlign: 'center'}}>
                        <CardContent>
                            {!(currentReservationData?.currentReservations?.length > 0) ? (
                                <Typography color="primary" component="div">
                                    {t('noCurrentReservations')}
                                </Typography>
                            ) : (
                                currentReservationData.currentReservations.map(reservation => (
                                    <div key={reservation.id}>
                                        <Typography color="primary" variant="body1" component="div">
                                            {formatDate(reservation.fromDate)} - {formatDate(reservation.toDate)}
                                        </Typography>
                                        {reservation.animals.map(animal => (
                                            <Typography key={animal.id} variant="body2" component="div" onClick={() => {
                                                setCurrentUuid(animal.uuid)
                                                setCurrentAnimalName(animal.name)
                                                handleOpen()
                                            }} sx={{
                                                cursor: "pointer",
                                                "&:hover": {
                                                    textDecoration: "underline",
                                                    color: "primary.main",
                                                }
                                            }} data-testid={`animal-attention-${animal.name}`}>
                                                {animal.name}{' '}
                                                {animal.sex === 'Female' ? (
                                                    <FemaleIcon style={{position: "relative", top: "5px"}} color="primary"/>
                                                ) : (
                                                    <MaleIcon style={{position: "relative", top: "5px"}} color="primary"/>
                                                )}
                                            </Typography>
                                        ))}
                                    </div>
                                ))
                            )}
                        </CardContent>
                    </Grid>
                </Card>
            </Grid>
            <Grid item xs={12} sm={4} sx={{mb: 2}}>
                <Card variant="outlined" sx={{p: 2, m: 2, height: '100%'}}>
                    <CardHeader title={<Typography color="secondary" variant="h5" align={"center"}>{t('upcomingReservations')}</Typography>}/>
                    <Grid container justifyContent="center" spacing={1} sx={{textAlign: 'center'}}>
                        <CardContent>
                            {!(upcomingReservationData?.upcomingReservations?.length > 0) ? (
                                <Typography color="primary" component="div">
                                    {t('noUpcomingReservations')}
                                </Typography>
                            ) : (
                                upcomingReservationData.upcomingReservations.map(reservation => (
                                    <div key={reservation.id}>
                                        <Typography color="primary" variant="body1" component="div">
                                            {formatDate(reservation.fromDate)} - {formatDate(reservation.toDate)}
                                        </Typography>
                                        {reservation.animals.map(animal => (
                                            <Typography key={animal.id} variant="body2" component="div" onClick={() => {
                                                setCurrentUuid(animal.uuid)
                                                handleOpen()
                                            }} sx={{
                                                cursor: "pointer",
                                                "&:hover": {
                                                    textDecoration: "underline",
                                                    color: "primary.main",
                                                }
                                            }}>
                                                {animal.name}{' '}
                                                {animal.sex === 'Female' ? (
                                                    <FemaleIcon style={{position: "relative", top: "5px"}} color="primary"/>
                                                ) : (
                                                    <MaleIcon style={{position: "relative", top: "5px"}} color="primary"/>
                                                )}
                                            </Typography>
                                        ))}
                                    </div>
                                ))
                            )}
                        </CardContent>
                    </Grid>
                </Card>
            </Grid>
            <Grid item xs={12} sm={4} sx={{mb: 2}}>
                <Card variant="outlined" sx={{p: 2, m: 2, height: '100%'}}>
                    <CardHeader title={<Typography color="secondary" variant="h5" align={"center"}>{t('specialAttention')}</Typography>}/>
                    <Grid container justifyContent="center" spacing={1} sx={{textAlign: 'center'}}>
                        <CardContent>
                            {(() => {
                                const attentionItems = currentReservationData?.currentReservations
                                    ?.flatMap(reservation => reservation.animals || [])
                                    .flatMap(animal => (animal.attentions || []).map(attention => ({animal, attention}))) || [];

                                if (attentionItems.length === 0) {
                                    return (
                                        <Typography color="primary" component="div">
                                            {t('noSpecialAttention')}
                                        </Typography>
                                    );
                                }

                                return attentionItems.map(({animal, attention}, index) => (
                                    <Typography key={`${animal.id}-${index}`} color="primary" variant="p" component="div">
                                        {animal.name} - {attention.subject}
                                    </Typography>
                                ));
                            })()}
                        </CardContent>
                    </Grid>
                </Card>
            </Grid>

            <Grid item xs={12} sm={4} sx={{mb: 2}}>
                <Card variant="outlined" sx={{p: 2, m: 2, height: '100%'}}>
                    <CardHeader title={<Typography color="secondary" variant="h5" align={"center"}>{t('checkInToday')}</Typography>}/>
                    <Grid container justifyContent="center" spacing={1} sx={{textAlign: 'center'}}>
                        <CardContent>
                            {todayCheckIns?.length === 0 ?
                                <Typography color="primary" component="div">
                                    {t('noCheckInToday')}
                                </Typography>
                                :
                                todayCheckIns
                                    .map((animal, i) => (
                                        <Typography key={`${animal.id}-${i}`} color="primary" component="div">
                                            {animal.name}
                                        </Typography>
                                    ))}
                        </CardContent>
                    </Grid>
                </Card>
            </Grid>

            <Grid item xs={12} sm={4} sx={{mb: 2}}>
                <Card variant="outlined" sx={{p: 2, m: 2, height: '100%'}}>
                    <CardHeader title={<Typography color="secondary" variant="h5" align={"center"}>{t('checkOutToday')}</Typography>}/>
                    <Grid container justifyContent="center" spacing={1} sx={{textAlign: 'center'}}>
                        <CardContent>
                            {todayCheckOuts?.length === 0 ?
                                <Typography color="primary" component="div">
                                    {t('noCheckOutToday')}
                                </Typography>
                                :
                                todayCheckOuts
                                    .map((animal, i) => (
                                        <Typography key={`${animal.id}-${i}`} color="primary" component="div">
                                            {animal.name}
                                        </Typography>
                                    ))}
                        </CardContent>
                    </Grid>
                </Card>
            </Grid>

            <AttentionModal
                open={open}
                animalName={currentAnimalName}
                onClose={handleClose}
                onSubmit={handleSubmitAttention}
                initialSubject={attentionSubject || ''}
            />

            <AppSnackbar openSnackbar={openSnackbar}
                         snackbarMessage={snackbarMessage}
                         setOpenSnackbar={setOpenSnackbar}
                         severity={severity}/>
        </Grid>);
};

export default withAuthenticationRequired(ReservationsDashboard, {
    onRedirecting: () => <Loading/>, returnTo: '/beheer/login'
});
